import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { NumericFormat } from "react-number-format";
import Swal from "sweetalert2";

import request from "../../../Helpers/API";
import { MesCosechaArray } from "../../../Arrays/MesCosechaArray";
import { CRM_ChipMultiSelect } from "./CRM_ChipMultiSelect";

const FORM_VACIO = {
  idcultivo: "",
  terreno: "",
  hectareas: "",
  messiembra: "",
  tipo_riego: [],
  mescosecha: "",
};

// Seguro de cosecha, rendimiento y precio por tonelada ya no se capturan en el modal —
// se siguen enviando al backend con estos valores fijos para no romper el endpoint existente.
// ciclo tampoco se captura ya (Mes siembra ahora usa su propio campo messiembra/idmessiembra)
const SEGURO_COSECHA_FIJO = "S";
const RENDIMIENTO_FIJO = 1;
const PRECIO_FIJO = 1;
const CICLO_FIJO = 1;

// Convierte "31,32" (idtipo_riego que llega del backend) a array de ids string, para el ChipMultiSelect
const parseIdsCSV = (csv) => (csv ? String(csv).split(",").map((v) => v.trim()).filter(Boolean) : []);

// Id de la opción de Riego que representa "Temporal" — determina el valor S/N que se manda
// al backend en el campo temporal cuando está entre los tipos de riego seleccionados
const ID_RIEGO_TEMPORAL = 34;

export const CRM_ModalCultivo = ({ open, onClose, idcliente, registro, onGuardado, opcionesCultivo = { terreno: [], riego: [] } }) => {
  const [form, setForm] = useState(FORM_VACIO);
  const [cultivosDisponibles, setCultivosDisponibles] = useState([]);
  const [busquedaCultivo, setBusquedaCultivo] = useState("");
  const [mostrarCultivos, setMostrarCultivos] = useState(false);
  const [posicionCultivos, setPosicionCultivos] = useState(null);
  const buscadorCultivoRef = useRef(null);
  const dropdownCultivoRef = useRef(null);

  // Catálogo real de cultivos — mismo origen que el legado (Cultivos/DropDownList)
  useEffect(() => {
    request
      .get("Cultivos/DropDownList")
      .then((result) => setCultivosDisponibles(result || []))
      .catch((error) => Swal.fire("CULTIVOS", JSON.stringify(error), "error"));
  }, []);

  // Al editar (registro > 0) se carga el detalle real del cultivo
  useEffect(() => {
    if (!open) return;
    if (!registro || registro <= 0) {
      setForm(FORM_VACIO);
      return;
    }
    request
      .get(`ClientesCultivo/ObtenerporOrden?idcliente=${Number(idcliente)}&registro=${Number(registro)}`)
      .then((result) => {
        setForm({
          idcultivo: result?.idcultivo ?? "",
          terreno: result?.idterreno || "",
          hectareas: result?.hectareas ?? "",
          messiembra: result?.idmessiembra ? String(result.idmessiembra).padStart(2, "0") : "",
          tipo_riego: parseIdsCSV(result?.idtipo_riego),
          mescosecha: result?.idmescosecha ? String(result.idmescosecha).padStart(2, "0") : "",
        });
      })
      .catch((error) => Swal.fire("CULTIVOS", JSON.stringify(error), "error"));
    setBusquedaCultivo("");
  }, [open, registro, idcliente]);

  const handleChange = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  const cultivoSeleccionado = cultivosDisponibles.find((o) => String(o.id) === String(form.idcultivo)) || null;

  const resultadosCultivo = mostrarCultivos
    ? cultivosDisponibles.filter((o) => (o.display || "").toLowerCase().includes(busquedaCultivo.toLowerCase()))
    : [];

  // El dropdown se renderiza en un portal hacia document.body (posición fixed calculada aquí),
  // para que el overflow del modal no lo recorte — mismo patrón que CRM_ChipMultiSelect
  const calcularPosicionCultivos = () => {
    if (!buscadorCultivoRef.current) return;
    const rect = buscadorCultivoRef.current.getBoundingClientRect();
    setPosicionCultivos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!mostrarCultivos) return;
    calcularPosicionCultivos();

    const handleReposicionar = () => calcularPosicionCultivos();
    const handleClickFuera = (e) => {
      const dentroDelBuscador = buscadorCultivoRef.current && buscadorCultivoRef.current.contains(e.target);
      const dentroDelDropdown = dropdownCultivoRef.current && dropdownCultivoRef.current.contains(e.target);
      if (!dentroDelBuscador && !dentroDelDropdown) setMostrarCultivos(false);
    };

    window.addEventListener("scroll", handleReposicionar, true);
    window.addEventListener("resize", handleReposicionar);
    document.addEventListener("mousedown", handleClickFuera);
    return () => {
      window.removeEventListener("scroll", handleReposicionar, true);
      window.removeEventListener("resize", handleReposicionar);
      document.removeEventListener("mousedown", handleClickFuera);
    };
  }, [mostrarCultivos]);

  const handleSeleccionarCultivo = (o) => {
    setForm((prev) => ({ ...prev, idcultivo: o.id }));
    setBusquedaCultivo("");
    setMostrarCultivos(false);
  };

  const handleQuitarCultivo = () => setForm((prev) => ({ ...prev, idcultivo: "" }));

  const handleAddRiego = (id) => setForm((prev) => ({ ...prev, tipo_riego: [...prev.tipo_riego, id] }));
  const handleRemoveRiego = (id) => setForm((prev) => ({ ...prev, tipo_riego: prev.tipo_riego.filter((v) => v !== id) }));

  // Campos numéricos con separador de miles mientras se escribe
  const handleChangeNumero = (campo) => (values) => {
    setForm((prev) => ({ ...prev, [campo]: values.value }));
  };

  // Cultivo, Hectáreas, Mes siembra y Mes cosecha son obligatorios — el resto puede quedar sin seleccionar
  const validar = () => {
    if (!form.idcultivo) { Swal.fire("CULTIVOS", "El cultivo es requerido", "warning"); return false; }
    if (!form.hectareas || isNaN(Number(form.hectareas))) { Swal.fire("CULTIVOS", "Las hectáreas deben ser numéricas", "warning"); return false; }
    if (!form.messiembra) { Swal.fire("CULTIVOS", "El mes de siembra es requerido", "warning"); return false; }
    if (!form.mescosecha) { Swal.fire("CULTIVOS", "El mes de cosecha es requerido", "warning"); return false; }
    return true;
  };

  const handleGuardar = () => {
    if (!validar()) return;

    const body = {
      idcliente: Number(idcliente),
      registro: Number(registro ?? -1),
      idcultivo: Number(form.idcultivo),
      terreno: form.terreno || "N",
      hectareas: Number(form.hectareas),
      seguro_cosecha: SEGURO_COSECHA_FIJO,
      ciclo: CICLO_FIJO,
      messiembra: Number(form.messiembra),
      tipo_riego: form.tipo_riego.join(","),
      temporal: form.tipo_riego.includes(String(ID_RIEGO_TEMPORAL)) ? "S" : "N",
      rendimiento: RENDIMIENTO_FIJO,
      precio: PRECIO_FIJO,
      mescosecha: Number(form.mescosecha),
      estatus: true,
    };

    request
      .post("ClientesCultivo", body)
      .then(async (result) => {
        await Swal.fire("CULTIVOS", result?.mensaje || "Cultivo guardado con éxito", "success");
        onGuardado(result?.listado);
        onClose();
      })
      .catch((error) => Swal.fire("CULTIVOS", JSON.stringify(error), "error"));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="crm-modal-card crm-modal-card-lg">
        <button type="button" className="crm-modal-cerrar" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </button>

        <h3 className="crm-modal-titulo">{registro > 0 ? "Editar cultivo" : "Agregar cultivo"}</h3>

        <div className="crm-modal-body">
          <div className="crm-detalle-grid crm-detalle-grid-1">
            <div className="crm-field">
              <label>Cultivo</label>
              {cultivoSeleccionado ? (
                <div className="crm-detalle-cliente-asociado">
                  <div>
                    <span className="asociado-nombre">{cultivoSeleccionado.display}</span>
                  </div>
                  <button type="button" className="btn-quitar-asociado" onClick={handleQuitarCultivo}>×</button>
                </div>
              ) : (
                <div className="crm-home-search-section" ref={buscadorCultivoRef}>
                  <div className={`crm-home-search-bar${resultadosCultivo.length > 0 ? " search-open" : ""}`}>
                    <SearchIcon className="crm-search-icon" />
                    <input
                      type="text"
                      className="crm-search-input"
                      placeholder="Buscar cultivo por nombre..."
                      value={busquedaCultivo}
                      onChange={(e) => setBusquedaCultivo(e.target.value)}
                      onFocus={() => setMostrarCultivos(true)}
                      autoComplete="off"
                    />
                  </div>

                  {resultadosCultivo.length > 0 && posicionCultivos && createPortal(
                    <div
                      ref={dropdownCultivoRef}
                      className="crm-search-dropdown crm-search-dropdown-fixed"
                      style={{ top: posicionCultivos.top, left: posicionCultivos.left, width: posicionCultivos.width }}
                    >
                      {resultadosCultivo.map((o) => (
                        <div
                          key={o.id}
                          className="crm-search-result"
                          onClick={() => handleSeleccionarCultivo(o)}
                        >
                          <div className="crm-res-info">
                            <span className="crm-res-nombre">{o.display}</span>
                          </div>
                        </div>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="crm-detalle-grid crm-detalle-grid-2">
            <div className="crm-field">
              <label>Terreno</label>
              <select className="crm-field-editable" value={form.terreno} onChange={handleChange("terreno")}>
                <option value="">Seleccionar</option>
                {opcionesCultivo.terreno.map((o) => <option key={o.id} value={o.id}>{o.valor}</option>)}
              </select>
            </div>
            <div className="crm-field">
              <label>Hectáreas</label>
              <NumericFormat
                className="crm-field-editable"
                value={form.hectareas}
                onValueChange={handleChangeNumero("hectareas")}
                thousandSeparator=","
                decimalSeparator="."
                decimalScale={2}
                allowNegative={false}
              />
            </div>
          </div>

          <div className="crm-detalle-grid crm-detalle-grid-2">
            <div className="crm-field">
              <label>Mes siembra</label>
              <select className="crm-field-editable" value={form.messiembra} onChange={handleChange("messiembra")}>
                <option value="">Seleccionar</option>
                {MesCosechaArray.map((o) => <option key={o.id} value={o.id}>{o.display}</option>)}
              </select>
            </div>
            <div className="crm-field">
              <label>Mes de cosecha</label>
              <select className="crm-field-editable" value={form.mescosecha} onChange={handleChange("mescosecha")}>
                <option value="">Seleccionar</option>
                {MesCosechaArray.map((o) => <option key={o.id} value={o.id}>{o.display}</option>)}
              </select>
            </div>
          </div>

          <CRM_ChipMultiSelect
            label="Tipo de riego"
            valores={form.tipo_riego}
            opciones={opcionesCultivo.riego.map((o) => ({ id: String(o.id), display: o.valor }))}
            editing
            onAdd={handleAddRiego}
            onRemove={handleRemoveRiego}
          />
        </div>

        <div className="crm-modal-buttons">
          <button type="button" className="btn-secundario-outline" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn-primario-solido" onClick={handleGuardar}>Guardar</button>
        </div>
      </div>
    </Modal>
  );
};
